var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getValidAccessToken, refreshAccessToken } from '@/config/tokenService';
export const useWebSocket = (url) => {
    const [ws, setWs] = useState(null);
    const [reconnectAttempt, setReconnectAttempt] = useState(0);
    const reconnectInterval = useRef(null);
    const heartbeatInterval = useRef(null);
    const websocketRef = useRef(null);
    const disconnectToastId = useRef(null);
    const authFailed = useRef(false);
    const connect = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (websocketRef.current &&
            (websocketRef.current.readyState === WebSocket.OPEN ||
                websocketRef.current.readyState === WebSocket.CONNECTING)) {
            return;
        }
        // After a rejected auth, force a token refresh before retrying.
        const token = authFailed.current ? yield refreshAccessToken() : yield getValidAccessToken();
        if (!token) {
            return; // Not signed in yet; connect() runs again on focus / after login reload.
        }
        if (websocketRef.current) {
            return; // Another connect() won the race while the token was being fetched.
        }
        const websocket = new WebSocket(url);
        websocketRef.current = websocket;
        let authenticated = false;
        // The token travels in the first message, never in the URL.
        websocket.onopen = () => {
            websocket.send(JSON.stringify({ type: 'auth', token }));
        };
        const handleAuthenticated = () => {
            authenticated = true;
            authFailed.current = false;
            setWs(websocket);
            setReconnectAttempt(0);
            if (disconnectToastId.current && toast.isActive(disconnectToastId.current)) {
                toast.dismiss(disconnectToastId.current);
            }
            disconnectToastId.current = null;
            if (reconnectInterval.current) {
                clearInterval(reconnectInterval.current);
                reconnectInterval.current = null;
            }
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
            heartbeatInterval.current = setInterval(() => {
                if (websocket.readyState === WebSocket.OPEN) {
                    websocket.send(JSON.stringify({ type: 'pong' }));
                }
            }, 30000); // Send a pong every 30 seconds
        };
        // addEventListener (not onmessage) so consumers can still assign ws.onmessage.
        websocket.addEventListener('message', (event) => {
            var _a;
            if (authenticated)
                return;
            try {
                if (((_a = JSON.parse(event.data)) === null || _a === void 0 ? void 0 : _a.type) === 'auth_ok') {
                    handleAuthenticated();
                }
            }
            catch (_b) {
                // ignore non-JSON frames before authentication
            }
        });
        websocket.onerror = (error) => {
        };
        websocket.addEventListener('close', (event) => {
            if (!authenticated) {
                authFailed.current = true;
            }
            if (!event.wasClean || !authenticated) {
                setReconnectAttempt((prev) => prev + 1);
            }
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
            if (websocketRef.current === websocket) {
                websocketRef.current = null;
            }
        });
        // Remove the onmessage handler from here
    }), [url]);
    useEffect(() => {
        connect();
        return () => {
            if (websocketRef.current) {
                websocketRef.current.close();
            }
            if (reconnectInterval.current) {
                clearInterval(reconnectInterval.current);
            }
            if (heartbeatInterval.current) {
                clearInterval(heartbeatInterval.current);
            }
        };
    }, [url, connect]);
    useEffect(() => {
        if (reconnectAttempt > 0) {
            if (reconnectInterval.current) {
                clearInterval(reconnectInterval.current);
            }
            reconnectInterval.current = setInterval(() => {
                connect();
            }, Math.min(30000, reconnectAttempt * 5000)); // Exponential backoff up to 30 seconds
        }
    }, [reconnectAttempt, connect, url]);
    // Handle visibility change and focus events
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                connect();
            }
        };
        const handleFocus = () => {
            connect();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [connect]);
    // Prompt user to reload if reconnection attempts fail
    useEffect(() => {
        if (reconnectAttempt > 5) {
            if (!disconnectToastId.current || !toast.isActive(disconnectToastId.current)) {
                disconnectToastId.current = toast.error('Connection to the server was lost. Trying to reconnect in the background. You can keep using the page.', { autoClose: false });
            }
        }
    }, [reconnectAttempt]);
    return { ws, reconnectAttempt };
};
